/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { Select, Tooltip } from "@xfrontend/xui-react-plus";
import { components as reactSelectComponents } from "react-select";
import { HeaderSelectFilterProps, SelectOption } from "../types";

const MAX_VISIBLE = 1;
const CHIP_MAX_WIDTH = 90;
const OVERFLOW_LIST_CLASS = "policy-overflow-list";

const OverflowTooltipGlobalStyle = createGlobalStyle`
  .tooltip-inner:has(.${OVERFLOW_LIST_CLASS}) {
    max-width: min(420px, 80vw);
  }
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 20px;
  margin-left: 4px;
  padding: 0 7px;
  border-radius: 999px;
  background: var(--x-purple-500, #494358);
  color: var(--x-gray-200, #f6f6f7);
  font-family: "Roboto Mono", monospace;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: var(--x-purple-400, #6a6280);
  }
`;

const OverflowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: max-content;
  max-width: min(420px, 80vw);
  padding: 2px 0;
`;

const OverflowItem = styled.div`
  font-size: 13px;
  line-height: 1.4;
  color: var(--x-gray-200, #f6f6f7);
  white-space: normal;
  overflow-wrap: anywhere;
`;

const CustomMultiValue = (props: any): JSX.Element | null => {
  const { index, getValue } = props;
  const values = (typeof getValue === "function" ? getValue() : []) as SelectOption[];
  if (index < MAX_VISIBLE) {
    return <reactSelectComponents.MultiValue {...props} />;
  }
  if (index === MAX_VISIBLE) {
    const overflow = values.slice(MAX_VISIBLE);
    if (overflow.length === 0) return null;
    return (
      <Tooltip
        trigger="click"
        placement="bottom"
        overlayComponent={(
          <OverflowList className={OVERFLOW_LIST_CLASS}>
            {overflow.map((v) => (
              <OverflowItem key={v.value}>{v.label}</OverflowItem>
            ))}
          </OverflowList>
        )}
      >
        <CountBadge
          onMouseDown={(e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {`+${overflow.length}`}
        </CountBadge>
      </Tooltip>
    );
  }
  return null;
};

const customStyle = {
  valueContainer: (base: any) => ({
    ...base,
    minHeight: "calc(42px - 4px)",
    padding: "5px 1rem",
    alignItems: "center",
    flexWrap: "nowrap",
    overflow: "hidden",
  }),
  multiValueLabel: (base: any) => ({
    ...base,
    maxWidth: CHIP_MAX_WIDTH,
  }),
};

const HeaderSelectFilter: FC<HeaderSelectFilterProps> = ({
  column: { columnDef },
  options,
  isMulti = true,
  selectedValues,
  onChange,
}) => {
  // Controlled only when selectedValues is supplied; otherwise leave `value` undefined so the
  // select stays uncontrolled for legacy callers. react-select wants the full option object(s).
  const resolveValue = (): SelectOption | SelectOption[] | null | undefined => {
    if (selectedValues === undefined) return undefined;
    const picked = options.filter((o) => selectedValues.includes(o.value));
    return isMulti ? picked : (picked[0] ?? null);
  };
  return (
    <>
      <OverflowTooltipGlobalStyle />
      <Select
        isMulti={isMulti}
        isClearable
        size="table"
        placeholder={columnDef.header}
        options={options}
        value={resolveValue()}
        withStickyHeader
        components={isMulti ? { MultiValue: CustomMultiValue } : undefined}
        customStyle={customStyle}
        onChange={(selected: unknown): void => {
          if (isMulti) {
            const values = (selected as SelectOption[] | null)?.map((o) => o.value) ?? [];
            onChange?.(values);
          } else {
            const single = selected as SelectOption | null;
            onChange?.(single ? [single.value] : []);
          }
        }}
      />
    </>
  );
};

export default HeaderSelectFilter;
